import { useMemo } from 'react';

import { useDeletePeerImage } from '@givebacks/api/store';
import { useFormListValidation } from 'core/Hooks';
import { ButtonMantine, Modal, Stack, notifications } from 'ui';

import { useUpdateCampaignPeer } from '../../../../api/new/campaigns/peers/update';
import { SetupPeerModalFormsType } from './SetupPeerPage.types';
import { useSetupPeerPageModalProvider } from './SetupPeerPageModalProvider';

const SetupPeerPageModalFooter = ({
  onClick,
  isShowSkip = true,
}: {
  /**
   * Optional click handler to override the default save and continue functionality
   */
  onClick?: () => void;
  isShowSkip?: boolean;
}) => {
  const { meta, openNextStep } = useSetupPeerPageModalProvider({});

  const { useGetForms, validateForms, useIsFormsDirty } =
    useFormListValidation<SetupPeerModalFormsType>();

  const { bannerImage, fundraisingDescription } = useGetForms();

  const campaignId = meta.campaign.uuid;
  const causeId = meta.campaign.cause_id;

  const isFormsDirty = useIsFormsDirty();

  // use meta peer type do determine which update/request to use
  const { mutate: updateParticipant, isPending: isLoading } = useUpdateCampaignPeer({
    peerType: 'participants',
  });
  const { mutateAsync: deletePeerImage } = useDeletePeerImage();

  const participant = meta.peer;

  const participantUpdateRequest = useMemo(() => {
    const request = {
      first_name: participant.first_name,
      last_name: participant.last_name,
      uuid: participant.uuid,
    };

    if (bannerImage?.values) {
      Object.assign(request, { image: bannerImage?.values?.file });
    }

    if (fundraisingDescription?.values?.description !== undefined) {
      Object.assign(request, { description: fundraisingDescription?.values?.description });
    }

    return request;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerImage?.values, fundraisingDescription?.values?.description]);

  const handleDeletePeerImage = async () => {
    if (bannerImage?.isDirty() && bannerImage.values.image === undefined) {
      if (bannerImage.values.file) return true; // If swapping the image, don't delete the old one, we'll just run the update.

      try {
        await deletePeerImage({
          peerId: participant.uuid,
          campaignId,
          causeId,
          peerType: 'participants',
        });
      } catch {
        throw new Error('Failed to delete peer image');
      }
    } else {
      return true;
    }
  };

  const handleSave = async () => {
    if (!causeId || !campaignId) return;
    if (validateForms().isValid) {
      if (isFormsDirty) {
        try {
          await handleDeletePeerImage();
        } catch {
          notifications.show({ variant: 'error', message: 'Could not update page' });
          return;
        }

        updateParticipant(
          {
            causeId,
            campaignId,
            ...participantUpdateRequest,
          },
          {
            onSuccess() {
              notifications.show({ variant: 'success', message: 'Page updated' });
              openNextStep();
            },
            onError() {
              notifications.show({ variant: 'error', message: 'Could not update page' });
            },
          }
        );
      } else {
        openNextStep();
      }
    }
  };

  return (
    <Modal.AffixedFooter>
      <Stack gap="xs">
        <ButtonMantine fullWidth onClick={onClick ?? handleSave} disabled={isLoading}>
          Save & Continue
        </ButtonMantine>
        {isShowSkip && (
          <ButtonMantine fullWidth variant="subtle" onClick={openNextStep}>
            Skip For Now
          </ButtonMantine>
        )}
      </Stack>
    </Modal.AffixedFooter>
  );
};

export { SetupPeerPageModalFooter };
